/**
 * Resolves all default values early from all possible sources
 * Checks: localStorage sizer settings -> ahuParams -> hardcoded defaults
 */
import { IAHUParams } from '../../../../models';

export function resolveDefaultValues(ahuParams: IAHUParams): {
  casingInsulationId: number | null;
  casingInnerlinerId: number | null;
  floorInnerlinerId: number | null;
  thickness: number | null;
  outerlinerId: number | null;
  underInsulationId: number | null;
  underlinerId: number | null;
} {
  // Default values
  const defaults: {
    casingInsulationId: number | null;
    casingInnerlinerId: number | null;
    floorInnerlinerId: number | null;
    thickness: number | null;
    outerlinerId: number | null;
    underInsulationId: number | null;
    underlinerId: number | null;
  } = {
    casingInsulationId: 56,
    casingInnerlinerId: 701,
    floorInnerlinerId: 697,
    thickness: 2,
    underlinerId: null,
    underInsulationId: null,
    outerlinerId: null,
  };

  // Check localStorage sizer settings
  let settingsFromStorage: any = {};
  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem('sizer_settings');
      if (stored) {
        settingsFromStorage = JSON.parse(stored);
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  // Resolve casing_insulation_id
  let casingInsulationId: number | undefined;
  if (settingsFromStorage.casing_insulation_id) {
    casingInsulationId = +settingsFromStorage.casing_insulation_id;
  } else if (settingsFromStorage.casing_insulation) {
    casingInsulationId = +settingsFromStorage.casing_insulation;
  } else casingInsulationId = 56;

  // Validate casing_insulation_id against ahuParams
  if (casingInsulationId && ahuParams?.casing?.insulations) {
    const insulation = ahuParams.casing.insulations.find(
      (el) => el.id === casingInsulationId,
    );
    if (insulation) {
      defaults.casingInsulationId = insulation.id;
      defaults.thickness = insulation.thickness || defaults.thickness;
    } else {
      // Invalid id from storage, use first available or default
      if (ahuParams.casing.insulations.length > 0) {
        defaults.casingInsulationId = ahuParams.casing.insulations[0].id;
        defaults.thickness =
          ahuParams.casing.insulations[0].thickness || defaults.thickness;
      }
    }
  } else if (
    ahuParams?.casing?.insulations &&
    ahuParams.casing.insulations.length > 0
  ) {
    // No value from storage, use first available from ahuParams
    defaults.casingInsulationId = ahuParams.casing.insulations[0].id;
    defaults.thickness =
      ahuParams.casing.insulations[0].thickness || defaults.thickness;
  }

  // Resolve casing_innerliner_id from ahuParams
  if (
    ahuParams?.casing?.liner_metal_sheets &&
    ahuParams.casing.liner_metal_sheets.length > 0
  ) {
    // Try to find default id (701) first, otherwise use first available
    const defaultLiner = ahuParams.casing.liner_metal_sheets.find(
      (e) => e.id === defaults.casingInnerlinerId,
    );
    defaults.casingInnerlinerId =
      defaultLiner?.id || ahuParams.casing.liner_metal_sheets[0].id;
  }

  // Resolve floor_innerliner_id from ahuParams
  if (
    ahuParams?.floor?.metal_sheets &&
    ahuParams.floor.metal_sheets.length > 0
  ) {
    // Try to find default id (697) first, otherwise use first available
    const defaultFloor = ahuParams.floor.metal_sheets.find(
      (e) => e.id === defaults.floorInnerlinerId,
    );
    defaults.floorInnerlinerId =
      defaultFloor?.id || ahuParams.floor.metal_sheets[0].id;
  } else {
    defaults.floorInnerlinerId = null;
  }

  // Resolve outerliner_id from ahuParams (calculate once)
  if (
    ahuParams?.casing?.metal_sheets &&
    ahuParams.casing.metal_sheets.length > 0
  ) {
    const outerliner = ahuParams.casing.metal_sheets.find((e) => e.id === 697);
    defaults.outerlinerId =
      outerliner?.id || ahuParams.casing.metal_sheets[0]?.id;
  } else {
    defaults.outerlinerId = null;
  }

  // Resolve under_insulation_id from ahuParams (calculate once)
  if (
    ahuParams?.floor?.under_insulations &&
    ahuParams.floor.under_insulations.length > 0
  ) {
    const underInsulation = ahuParams.floor.under_insulations.find(
      (e) => e.id === 56,
    );
    defaults.underInsulationId =
      underInsulation?.id || ahuParams.floor.under_insulations[0]?.id;
  } else {
    defaults.underInsulationId = null;
  }

  // Resolve under_liner_id from ahuParams (calculate once)
  if (
    ahuParams?.floor?.under_liner_metal_sheets &&
    ahuParams.floor.under_liner_metal_sheets.length > 0
  ) {
    const underLiner = ahuParams.floor.under_liner_metal_sheets.find(
      (e) => e.id === 703,
    );
    defaults.underlinerId =
      underLiner?.id || ahuParams.floor.under_liner_metal_sheets[0]?.id;
  } else {
    defaults.underlinerId = null;
  }

  return defaults;
}
